import { useState, useCallback, useEffect, useRef } from 'react';

interface ResizeHandleProps {
  direction: 'horizontal' | 'vertical';
  onResize: (delta: number) => void;
  onResizeEnd?: () => void;
  className?: string;
}

/**
 * ResizeHandle component for panel resizing
 * Provides visual feedback and handles drag interactions
 * Requirements: 8.1 - Show resize cursor on hover, add visual indicators, handle mouse events
 */
export function ResizeHandle({ 
  direction, 
  onResize, 
  onResizeEnd,
  className = '' 
}: ResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const startPosRef = useRef<number>(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startPosRef.current = direction === 'horizontal' ? e.clientX : e.clientY;
  }, [direction]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const currentPos = direction === 'horizontal' ? e.clientX : e.clientY;
      const delta = currentPos - startPosRef.current;
      startPosRef.current = currentPos;
      onResize(delta);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      onResizeEnd?.();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, direction, onResize, onResizeEnd]);

  const cursorClass = direction === 'horizontal' ? 'cursor-ew-resize' : 'cursor-ns-resize';
  const sizeClass = direction === 'horizontal' ? 'w-1 h-full' : 'h-1 w-full';
  const hoverClass = direction === 'horizontal' 
    ? 'hover:w-2 hover:bg-[#6366f1]/50' 
    : 'hover:h-2 hover:bg-[#6366f1]/50';

  return (
    <div
      className={`
        ${sizeClass}
        ${cursorClass}
        ${hoverClass}
        bg-[#2a2a2e]
        transition-all
        duration-150
        relative
        group
        ${isDragging ? 'bg-[#6366f1] z-50' : ''}
        ${className}
      `}
      onMouseDown={handleMouseDown}
    >
      {/* Visual indicator on hover */}
      <div 
        className={`
          absolute
          ${direction === 'horizontal' ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-8' : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-1 w-8'}
          bg-[#6366f1]
          rounded-full
          opacity-0
          group-hover:opacity-100
          transition-opacity
          duration-150
          pointer-events-none
        `}
      />
    </div>
  );
}
